// import { ViewportCoordinates, NodeInfo } from './shared/model/docavea-viewer-interface';

// function listSelectedNodes(selection) {
//     // for debugging purpose, list all selected nodes
//     const nodeTypes = NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT;
//     const range = selection.getRangeAt(0);  // Use range to get ancestor
//     const walker = document.createTreeWalker(range.commonAncestorContainer, nodeTypes, null);
//     range.detach();

//     // Start walker
//     let nextNode = walker.currentNode = selection.anchorNode;
//     const endNode = selection.focusNode;

//     console.log('Selected node dump:');
//     do {
//         console.log(nextNode);
//         nextNode = walker.nextNode();
//         if (nextNode === null) console.log('breaking due to nextNode === null');
//     } while (nextNode !== null && !nextNode.isEqualNode(endNode));
// }

// Measure a range's client rect, correcting for CSS transforms on the enclosing text layer span.
// pdf.js stretches each span with scaleX() to match the canvas (word-spaced/justified text gets
// factors far from 1), but Chromium's Range.getBoundingClientRect() ignores transforms. We derive
// the span's effective horizontal factor by comparing its element rect (transform-aware) with a
// range over its full contents (transform-unaware), then map the measured rect through it.
// If the browser applies transforms to range rects, the factor becomes 1 and this is a no-op.
function rangeClientRect(range: Range): { left: number, top: number, width: number, height: number } {
    const re = range.getBoundingClientRect();
    const span = range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement;
    if (span instanceof Element) {
        const spanRe = span.getBoundingClientRect();
        const fullRange = document.createRange();
        fullRange.selectNodeContents(span);
        const rawRe = fullRange.getBoundingClientRect();
        fullRange.detach();
        if (rawRe.width > 0 && spanRe.width > 0) {
            const factor = spanRe.width / rawRe.width;
            return {
                left: spanRe.left + (re.left - rawRe.left) * factor,
                top: re.top,
                width: re.width * factor,
                height: re.height,
            };
        }
    }
    return { left: re.left, top: re.top, width: re.width, height: re.height };
}

export function selectionHandler(clearSelection: boolean) {
    // https://superdevelopment.com/2016/02/08/working-with-the-html-selection-api/
    const selection = window.getSelection() as Selection;
    const selectionRectangels: any[] = [];
    let selectionText: string = '';
    if (!selection.isCollapsed) {
        // we have a non-zero length selection

        // listSelectedNodes(selection);  // Uncomment for debugging

        let startNode = selection.anchorNode;
        let startOffset = selection.anchorOffset;
        let endNode = selection.focusNode;
        let endOffset = selection.focusOffset;

        if (startNode instanceof Element) {
            // if it is an element then get the text
            startNode = startNode.childNodes[startOffset];
            startOffset = 0;
        }
        if (endNode instanceof Element) {
            // if it is an element then get the text
            endNode = endNode.childNodes[endOffset];
            endOffset = 0;
        }

        // find selecection direction https://stackoverflow.com/questions/9180405/detect-direction-of-user-selection-with-javascript$
        const position = (selection.anchorNode as Node).compareDocumentPosition(selection.focusNode as Node);
        if (!position && selection.anchorOffset > selection.focusOffset || position === Node.DOCUMENT_POSITION_PRECEDING) {
            // if backwards swap start and end nodes
            const tempStartNode = startNode;
            const tempStartOffset = startOffset;
            startNode = endNode;
            startOffset = endOffset;
            endNode = tempStartNode;
            endOffset = tempStartOffset;
        }

        // Get first line selection rect
        let firstNode = startNode as Text;
        firstNode.splitText(startOffset);
        firstNode = firstNode.nextSibling as Text;
        if (!position) {
            // if start and end nodes are the same we remove text not selected in the end
            (firstNode).splitText(endOffset - startOffset);
        }
        selectionText += firstNode?.textContent;
        // Get first line selected text bounding rectangle
        let range = document.createRange();
        range.selectNode(firstNode);
        let clientRe = rangeClientRect(range); // creating simple array because ClientRect has readonly members!?
        selectionRectangels.push({ left: clientRe.left, top: clientRe.top, width: clientRe.width, height: clientRe.height });
        range.detach();

        if (position) {
            // start and end nodes are different so we need to setup a walker to get the rest of the selection rectangles
            const nodeTypes = NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT;
            range = selection.getRangeAt(0);  // Use range to get ancestor
            const walker = document.createTreeWalker(range.commonAncestorContainer, nodeTypes, null);
            range.detach();

            // Start walker
            let nextNode = walker.currentNode = firstNode;
            nextNode = walker.nextNode() as Text;  // We already calculated the rectangle for the first node so skip that
            // Note: reference equality, NOT isEqualNode() — pdf.js emits many spans with identical
            // content (e.g. single-space spans in justified text), and content comparison would stop
            // the walk at the first look-alike, dropping the rest of the selection.
            while (nextNode !== null && nextNode !== endNode) {
                if (nextNode.nodeType === Node.TEXT_NODE) {
                    selectionText += nextNode?.textContent;
                    range.selectNode(nextNode);
                    clientRe = rangeClientRect(range);
                    selectionRectangels.push({ left: clientRe.left, top: clientRe.top, width: clientRe.width, height: clientRe.height });
                    range.detach();
                }
                nextNode = walker.nextNode() as Text;
            }
            // Get last line selection rect if it is text
            if (nextNode !== null && nextNode.nodeType === Node.TEXT_NODE) {
                selectionText += nextNode?.textContent;
                (endNode as Text).splitText(endOffset);
                range.selectNode(endNode as Text);
                clientRe = rangeClientRect(range);
                selectionRectangels.push({ left: clientRe.left, top: clientRe.top, width: clientRe.width, height: clientRe.height });
                range.detach();
            }
        }
    }
    // clear DOM selection
    if (clearSelection) {
        selection.removeAllRanges();
    }

    return { selectionRectangels: mergeSelectionRectangles(selectionRectangels), selectionText };
}

// Merge consecutive selection rects on the same text line into one. pdf.js renders justified
// text as per-word spans with unstretched single-space spans between them, so raw per-node rects
// leave a gap at every justified space (and per-word rects bloat the stored markup). Gaps up to
// one line-height are bridged; anything larger (e.g. a column gutter) stays separate.
function mergeSelectionRectangles(rects: any[]): any[] {
    const merged: any[] = [];
    for (const r of rects) {
        const prev = merged[merged.length - 1];
        const sameLine = prev &&
            (Math.min(prev.top + prev.height, r.top + r.height) - Math.max(prev.top, r.top)) > 0.5 * Math.min(prev.height, r.height);
        const gap = prev ? r.left - (prev.left + prev.width) : Infinity;
        if (sameLine && gap <= Math.max(prev.height, r.height)) {
            const right = Math.max(prev.left + prev.width, r.left + r.width);
            const bottom = Math.max(prev.top + prev.height, r.top + r.height);
            prev.left = Math.min(prev.left, r.left);
            prev.top = Math.min(prev.top, r.top);
            prev.width = right - prev.left;
            prev.height = bottom - prev.top;
        } else {
            merged.push({ left: r.left, top: r.top, width: r.width, height: r.height });
        }
    }
    return merged;
}

export function removeBadRectangles(selectionRectangels: any[], x1: number, y1: number, x2: number, y2: number) {
    // // remove bad rectangles (mouseUp between text lines create rectangles left/above 0,0)
    // const clientRect = canvasNo == 1 ? document.getElementById('canvasDivLeft').getClientRects() : document.getElementById('canvasDivRight').getClientRects();
    // for (let idx = selectionRectangels.length - 1; idx >= 0; idx--) {
    //     if (selectionRectangels[idx].left < clientRect[0].left || selectionRectangels[idx].top < clientRect[0].top)
    //         selectionRectangels.splice(idx, 1);
    // };

    // limit selection to mouse points if browser-generated selection starts or ends too far above/below mouse points; this happen if mouseUp happens over non-text
    // note that other rects are allowed to be above/below mouse points (in case of multi columns)
    // assumes rect order follow flow, which is likely true as rects/elements comes from interconnected text in the pdf data;
    // apparently OCR-pdf try to reconstruct this (columns etc.)
    // (coordinates are relative to browser window)
    if (Math.abs(selectionRectangels[0].top - y1) > 20 ||
        Math.abs(selectionRectangels[selectionRectangels.length - 1].top + selectionRectangels[selectionRectangels.length - 1].height - y2) > 20) {
        // console.log("limit selection");
        // find rects closest to mouse points
        let idx1 = 0;
        let idx2 = selectionRectangels.length - 1;
        let distMinimum1 = Number.MAX_VALUE;
        let distMinimum2 = Number.MAX_VALUE;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        selectionRectangels.forEach((_rectangle, idx, _array) => {
            const rectLeft = selectionRectangels[idx].left;
            const rectRight = selectionRectangels[idx].left + selectionRectangels[idx].width;
            const rectTop = selectionRectangels[idx].top;
            const rectBottom = selectionRectangels[idx].top + selectionRectangels[idx].height;
            // mouse point is in rect
            if (rectLeft < x1 && rectRight > x1 && rectTop < y1 && rectBottom > y1) {
                idx1 = idx;
                distMinimum1 = -1;
            }
            if (rectLeft < x2 && rectRight > x2 && rectTop < y2 && rectBottom > y2) {
                idx2 = idx;
                distMinimum2 = -1;
            }
            // rect closest to mouse point
            let dist1: number;
            if (rectLeft < x1 && rectRight > x1) { // rect and point aligned vertically (power 2 to avoid sqrt for euclidean distance)
                dist1 = Math.pow(Math.min(Math.abs(rectTop - y1), Math.abs(rectBottom - y1)), 2);
            } else {
                if (rectTop < y1 && rectBottom > y1) { // rect and point aligned horizontally (power 2 to avoid sqrt for euclidean distance)
                    dist1 = Math.pow(Math.min(Math.abs(rectLeft - x1), Math.abs(rectRight - x1)), 2);
                } else {
                    dist1 = Math.min( // euclidean distance
                        (rectLeft - x1) * (rectLeft - x1) + (rectTop - y1) * (rectTop - y1),
                        (rectLeft - x1) * (rectLeft - x1) + (rectBottom - y1) * (rectBottom - y1),
                        (rectRight - x1) * (rectRight - x1) + (rectTop - y1) * (rectTop - y1),
                        (rectRight - x1) * (rectRight - x1) + (rectBottom - y1) * (rectBottom - y1));
                }
            }
            if (distMinimum1 > dist1) {
                distMinimum1 = dist1;
                idx1 = idx;
            }
            let dist2: number;
            if (rectLeft < x2 && rectRight > x2) { // rect and point aligned vertically (power 2 to avoid sqrt for euclidean distance)
                dist2 = Math.pow(Math.min(Math.abs(rectTop - y2), Math.abs(rectBottom - y2)), 2);
            } else {
                if (rectTop < y2 && rectBottom > y2) {// rect and point aligned horizontally (power 2 to avoid sqrt for euclidean distance)
                    dist2 = Math.pow(Math.min(Math.abs(rectLeft - x2), Math.abs(rectRight - x2)), 2);
                } else {
                    dist2 = Math.min( // euclidean distance
                        (rectLeft - x2) * (rectLeft - x2) + (rectTop - y2) * (rectTop - y2),
                        (rectLeft - x2) * (rectLeft - x2) + (rectBottom - y2) * (rectBottom - y2),
                        (rectRight - x2) * (rectRight - x2) + (rectTop - y2) * (rectTop - y2),
                        (rectRight - x2) * (rectRight - x2) + (rectBottom - y2) * (rectBottom - y2));
                }
            }
            if (distMinimum2 > dist2) {
                distMinimum2 = dist2;
                idx2 = idx;
            }
        });
        // console.log(idx1, idx2, distMinimum1, distMinimum2);
        idx1 < idx2 ? selectionRectangels = selectionRectangels.slice(idx1, idx2 + 1) : selectionRectangels = selectionRectangels.slice(idx2, idx1 + 1);
    }

    return selectionRectangels;
}

export const canSelectText = (pageNumber: number) => {
    const pages = document.getElementsByClassName('page') as any;

    let loaded = false;
    let canSelectText = false;
    for (const _page of pages) {
        if (+_page.dataset.pageNumber === pageNumber) {
            const page: HTMLElement = _page;
            if (page.dataset.loaded) {
                loaded = true;
                const textLayers = page.getElementsByClassName('textLayer');
                const spans = textLayers[0].getElementsByTagName('span') as any;
                for (const span of spans) {
                    if (span.firstChild.data.length > 0) {
                        canSelectText = true;
                        break;
                    }
                }
            } 
        }
    }

    return { loaded, canSelectText };
};
/*
// Function to retrieve node information
function getNodeInfo(node: Node): NodeInfo {
    const element = node as Element;
    const rect = element.getBoundingClientRect();
    const text = element.textContent || '';

    const viewportCoordinates: ViewportCoordinates = {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
    };

    return {
        text,
        coordinates: viewportCoordinates,
    };
}

// Function to iterate through nodes within a range and collect node information
export function iterateNodes(): NodeInfo[] {
    const range = window.getSelection()!.getRangeAt(0);
    const startNode = range.startContainer;
    const endNode = range.endContainer;
    const startOffset = range.startOffset;
    const endOffset = range.endOffset;

    const nodeInfoArray: NodeInfo[] = [];

    // If the range starts and ends in the same text node, process it directly
    if (
        startNode === endNode &&
        startNode.nodeType === Node.TEXT_NODE &&
        startNode.parentElement
    ) {
        const nodeInfo = getNodeInfo(startNode.parentElement);
        nodeInfoArray.push(nodeInfo);
    } else {
        iterateChildNodes(startNode, startOffset, endNode, endOffset, nodeInfoArray);
    }
    
    return nodeInfoArray;
}

// Recursive function to iterate through child nodes
function iterateChildNodes(
    startNode: Node,
    startOffset: number,
    endNode: Node,
    endOffset: number,
    nodeInfoArray: NodeInfo[]
): void {
    let currentNode: Node | null = startNode;
    while (currentNode) {
        const nextNode = getNextNode(currentNode, endNode);

        if (
            currentNode.nodeType === Node.TEXT_NODE &&
            currentNode.parentElement
        ) {
            const nodeInfo = getNodeInfo(currentNode.parentElement);
            nodeInfoArray.push(nodeInfo);
        }

        // If we have reached the end node, exit the loop
        if (currentNode === endNode) {
            break;
        }

        currentNode = nextNode;
    }
}

// Function to get the next sibling or ancestor sibling node
function getNextNode(currentNode: Node, endNode: Node): Node | null {
    if (currentNode.firstChild) {
        return currentNode.firstChild;
    }

    while (currentNode) {
        if (currentNode === endNode || currentNode.nextSibling) {
            return currentNode.nextSibling;
        }
        currentNode = currentNode.parentNode!;
    }

    return null;
}
*/
